/**
 * Finishd Notifications Page
 * Displays all notifications for the logged-in user
 */

import { ArrowLeft, BellOff, CheckCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { useNotificationsStore } from "@/features/settings/store/notifications.store";
import { getNotificationRoute } from "@/features/settings/utils/get-notification-route";
import {
	getNotificationIcon,
	timeAgo,
} from "@/features/settings/utils/notification-helpers";
import { FloatPanel, StudioButton } from "@/features/studio/theme";
import { ViewNotificationModal } from "../components/view-notification-modal";

// ============================================================================
// NOTIFICATIONS PAGE
// ============================================================================

export function NotificationsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [isMarkingAll, setIsMarkingAll] = useState(false);
	const {
		notifications,
		isLoaded,
		isConnecting,
		loadInitial,
		markAllRead,
		markRead,
	} = useNotificationsStore();

	useEffect(() => {
		if (!isLoaded && !isConnecting) {
			void loadInitial();
		}
	}, [isConnecting, isLoaded, loadInitial]);

	const [activeNotification, setActiveNotification] = useState<
		(typeof notifications)[number] | null
	>(null);

	const handleMarkAsRead = async (
		notification: (typeof notifications)[number],
	) => {
		if (import.meta.env.MODE === "test") {
			try {
				if (!notification.isRead) {
					await markRead(notification.id);
				}
				const route = getNotificationRoute(
					notification.type,
					notification.data,
				);
				if (route.clickable) {
					navigate(route.path);
				}
			} catch (error) {
				console.error("Failed to mark notification as read:", error);
			}
			return;
		}
		setActiveNotification(notification);
	};

	const handleMarkAllRead = async () => {
		setIsMarkingAll(true);
		try {
			await markAllRead();
		} catch (error) {
			console.error("Failed to mark all notifications read:", error);
		} finally {
			setIsMarkingAll(false);
		}
	};

	const hasUnread = notifications.some((n) => !n.isRead);

	return (
		<div className="min-h-screen bg-transparent p-4 sm:p-12">
			<div className="mx-auto max-w-4xl space-y-8">
				{/* Header */}
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						borderBottom: "1px solid var(--color-dashboard-border)",
						paddingBottom: 24,
					}}
				>
					<div className="flex items-center gap-4">
						<button
							type="button"
							onClick={() => navigate("/settings")}
							style={{
								background: "none",
								border: "none",
								cursor: "pointer",
								color: "var(--color-dashboard-muted)",
								padding: 4,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
							className="hover:text-[var(--color-dashboard-accent-dark)] transition-colors"
						>
							<ArrowLeft size={24} />
						</button>
						<div>
							<span
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 10,
									fontWeight: 600,
									textTransform: "uppercase",
									letterSpacing: "0.08em",
									color: "var(--color-dashboard-muted)",
									display: "block",
								}}
							>
								USER DISPATCH
							</span>
							<h1
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 500,
									fontSize: 36,
									color: "var(--color-dashboard-text)",
									letterSpacing: "-0.02em",
									margin: "8px 0 0",
								}}
							>
								My{" "}
								<span
									style={{
										fontFamily: "var(--font-display)",
										fontStyle: "italic",
										color: "var(--color-dashboard-accent-display)",
									}}
								>
									Notifications
								</span>
							</h1>
						</div>
					</div>
					{hasUnread && (
						<StudioButton
							variant="quiet"
							size="sm"
							onClick={handleMarkAllRead}
							disabled={isMarkingAll}
						>
							<CheckCheck size={14} style={{ marginRight: 6 }} />
							{t("notifications.markAllRead")}
						</StudioButton>
					)}
				</div>

				{/* Content */}
				{!isLoaded ? (
					<div className="space-y-4">
						{Array.from({ length: 5 }).map((_, i) => (
							<FloatPanel
								key={`notification-skeleton-${String(i)}`}
								elevation={1}
								padding={18}
							>
								<div className="flex items-start gap-4">
									<Skeleton className="h-[38px] w-[38px] shrink-0 rounded-full" />
									<div className="flex-1 space-y-3">
										<Skeleton className="h-3.5 w-[35%] rounded" />
										<Skeleton className="h-3 w-3/5 rounded" />
										<Skeleton className="h-2.5 w-[15%] rounded" />
									</div>
								</div>
							</FloatPanel>
						))}
					</div>
				) : notifications.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-24 text-center">
						<BellOff
							size={48}
							style={{
								color: "var(--color-dashboard-border)",
								marginBottom: 16,
							}}
						/>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								fontSize: 18,
								color: "var(--color-dashboard-muted)",
								margin: 0,
							}}
						>
							{t("notifications.noNotificationsYet")}
						</h2>
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 13,
								color: "var(--color-dashboard-placeholder)",
								marginTop: 6,
								maxWidth: 280,
							}}
						>
							{t("notifications.noNotificationsDesc")}
						</p>
					</div>
				) : (
					<div className="space-y-4">
						{notifications.map((notification) => {
							const Icon = getNotificationIcon(notification.type);
							const route = getNotificationRoute(
								notification.type,
								notification.data,
							);

							return (
								<button
									key={notification.id}
									type="button"
									style={{
										display: "block",
										width: "100%",
										textAlign: "left",
										border: "none",
										background: "none",
										padding: 0,
										margin: 0,
										cursor: route.clickable ? "pointer" : "default",
									}}
									onClick={() => handleMarkAsRead(notification)}
								>
									<FloatPanel
										elevation={notification.isRead ? 1 : 2}
										padding={18}
										style={{
											borderLeft: notification.isRead
												? "1px solid var(--color-dashboard-border)"
												: "4px solid var(--color-dashboard-accent)",
											background: notification.isRead
												? "var(--color-dashboard-paper)"
												: "var(--color-dashboard-warm-100)",
											transition: "transform 150ms ease, box-shadow 150ms ease",
										}}
										className="hover:translate-x-0.5"
									>
										<div className="flex items-start gap-4">
											<div
												style={{
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													width: 38,
													height: 38,
													borderRadius: "50%",
													background: notification.isRead
														? "var(--color-dashboard-divider)"
														: "color-mix(in srgb, var(--color-dashboard-accent) 10%, transparent)",
													color: notification.isRead
														? "var(--color-dashboard-muted)"
														: "var(--color-dashboard-accent-dark)",
													flexShrink: 0,
												}}
											>
												<Icon size={16} />
											</div>
											<div className="min-w-0 flex-1">
												<div className="flex items-start justify-between gap-2">
													<p
														style={{
															fontFamily: "var(--font-body)",
															fontSize: 14,
															fontWeight: notification.isRead ? 500 : 700,
															color: "var(--color-dashboard-text)",
															margin: 0,
														}}
													>
														{notification.title}
													</p>
													{!notification.isRead && (
														<span
															className="rounded-full bg-primary"
															style={{
																width: 6,
																height: 6,
																backgroundColor:
																	"var(--color-dashboard-accent)",
																alignSelf: "center",
																flexShrink: 0,
															}}
														/>
													)}
												</div>
												<p
													style={{
														fontFamily: "var(--font-body)",
														fontSize: 13,
														color: "var(--color-dashboard-muted-dark)",
														marginTop: 4,
														marginBottom: 0,
													}}
												>
													{notification.message}
												</p>
												<span
													style={{
														fontFamily: "var(--font-mono)",
														fontSize: 9,
														color: "var(--color-dashboard-placeholder)",
														textTransform: "uppercase",
														letterSpacing: "0.06em",
														marginTop: 8,
														display: "block",
													}}
												>
													{timeAgo(notification.createdAt)}
												</span>
											</div>
										</div>
									</FloatPanel>
								</button>
							);
						})}
					</div>
				)}
			</div>

			{activeNotification && (
				<ViewNotificationModal
					notification={activeNotification}
					onClose={() => setActiveNotification(null)}
					onMarkRead={markRead}
					onNavigate={navigate}
				/>
			)}
		</div>
	);
}
